import {
  clearStoredAppHandle,
  clearStoredAppMetaValue,
  exportActiveProfileBackup,
  getActiveBudgetScope,
  getStoredAppHandle,
  getStoredAppMetaValue,
  initializeWalletWatchDb,
  setStoredAppHandle,
  setStoredAppMetaValue,
  subscribeProfileDataChanges
} from "./walletWatchDb.js";

const PROFILE_BACKUP_LAST_SAVED_AT_PREFIX = "profileBackupLastSavedAt";
const PROFILE_BACKUP_INTERVAL_PREFIX = "profileBackupInterval";
const PROFILE_BACKUP_NEEDS_ATTENTION_PREFIX = "profileBackupNeedsAttention";
const PROFILE_BACKUP_DIRTY_SINCE_PREFIX = "profileBackupDirtySince";
export const BACKUP_ATTENTION_CHANGED_EVENT = "wallet-watch:backup-attention-changed";
const DEFAULT_PROFILE_BACKUP_INTERVAL = "1h";
const AUTO_BACKUP_CHANGE_DELAY_MS = 15 * 1000;
const PROFILE_BACKUP_INTERVAL_MS = {
  "15m": 15 * 60 * 1000,
  "30m": 30 * 60 * 1000,
  "1h": 60 * 60 * 1000,
  "4h": 4 * 60 * 60 * 1000,
  "1d": 24 * 60 * 60 * 1000
};
const scheduledProfileBackups = new Map();

function profileBackupHandleKey(profileId) {
  return `profileBackupHandle:${profileId}`;
}

function profileBackupLastSavedAtKey(profileId) {
  return `${PROFILE_BACKUP_LAST_SAVED_AT_PREFIX}:${profileId}`;
}

function profileBackupIntervalKey(profileId) {
  return `${PROFILE_BACKUP_INTERVAL_PREFIX}:${profileId}`;
}

function profileBackupNeedsAttentionKey(profileId) {
  return `${PROFILE_BACKUP_NEEDS_ATTENTION_PREFIX}:${profileId}`;
}

function profileBackupDirtySinceKey(profileId) {
  return `${PROFILE_BACKUP_DIRTY_SINCE_PREFIX}:${profileId}`;
}

function formatBackupTimestamp(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day}_${hours}-${minutes}-${seconds}`;
}

async function getWritePermissionStatus(handle) {
  if (typeof handle?.queryPermission !== "function" || typeof handle?.requestPermission !== "function") {
    return "granted";
  }

  return handle.queryPermission({ mode: "readwrite" });
}

async function requestWritePermission(handle) {
  if (typeof handle?.requestPermission !== "function") {
    return "granted";
  }

  return handle.requestPermission({ mode: "readwrite" });
}

async function setProfileBackupAttention(profileId, needsAttention) {
  if (needsAttention) {
    await setStoredAppMetaValue(profileBackupNeedsAttentionKey(profileId), true);
    dispatchBackupAttentionChanged(profileId, true);
    return true;
  }

  await clearStoredAppMetaValue(profileBackupNeedsAttentionKey(profileId));
  dispatchBackupAttentionChanged(profileId, false);
  return false;
}

async function markProfileBackupDirty(profileId) {
  if (!profileId) {
    return;
  }

  await setStoredAppMetaValue(profileBackupDirtySinceKey(profileId), new Date().toISOString());
}

async function clearProfileBackupDirty(profileId, dirtyBeforeOrAt = null) {
  if (!profileId) {
    return;
  }

  if (dirtyBeforeOrAt) {
    const dirtySince = await getStoredAppMetaValue(profileBackupDirtySinceKey(profileId));

    if (dirtySince && String(dirtySince) > String(dirtyBeforeOrAt)) {
      return;
    }
  }

  await clearStoredAppMetaValue(profileBackupDirtySinceKey(profileId));
}

function dispatchBackupAttentionChanged(profileId, needsAttention) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }

  window.dispatchEvent(new CustomEvent(BACKUP_ATTENTION_CHANGED_EVENT, {
    detail: {
      profileId,
      needsAttention
    }
  }));
}

function safeBackupProfileName(profileName, fallbackProfileId) {
  return (
    String(profileName ?? fallbackProfileId)
      .trim()
      .toLocaleLowerCase()
      .replace(/[^a-z0-9_-]+/g, "-")
      .replace(/^-+|-+$/g, "")
      || fallbackProfileId
  );
}

export function buildProfileBackupFileName(profileName, profileId, year, timestamp = new Date()) {
  return `wallet-watch-backup-${safeBackupProfileName(profileName, profileId)}-${year}-${formatBackupTimestamp(timestamp)}.json`;
}

function normalizeBackupInterval(interval) {
  const value = String(interval ?? "").trim();
  return Object.hasOwn(PROFILE_BACKUP_INTERVAL_MS, value)
    ? value
    : DEFAULT_PROFILE_BACKUP_INTERVAL;
}

export async function getActiveProfileBackupTarget() {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();
  const handle = await getStoredAppHandle(profileBackupHandleKey(scope.profileId));
  const lastSavedAt = await getStoredAppMetaValue(profileBackupLastSavedAtKey(scope.profileId));
  const interval = normalizeBackupInterval(await getStoredAppMetaValue(profileBackupIntervalKey(scope.profileId)));
  const needsAttention = Boolean(await getStoredAppMetaValue(profileBackupNeedsAttentionKey(scope.profileId)));
  const dirtySince = await getStoredAppMetaValue(profileBackupDirtySinceKey(scope.profileId));
  const permission = handle
    ? await getWritePermissionStatus(handle)
    : "prompt";

  return {
    profileId: scope.profileId,
    year: scope.year,
    handle,
    lastSavedAt: lastSavedAt ?? null,
    interval,
    needsAttention,
    dirtySince: dirtySince ?? null,
    permission
  };
}

export async function getProfileBackupNeedsAttention(profileId) {
  await initializeWalletWatchDb();

  if (!profileId) {
    return false;
  }

  return Boolean(await getStoredAppMetaValue(profileBackupNeedsAttentionKey(profileId)));
}

export async function setProfileBackupAttentionState(profileId, needsAttention) {
  await initializeWalletWatchDb();

  if (!profileId) {
    return false;
  }

  return setProfileBackupAttention(profileId, needsAttention);
}

export async function getActiveProfileBackupInterval() {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();
  const storedValue = await getStoredAppMetaValue(profileBackupIntervalKey(scope.profileId));
  return normalizeBackupInterval(storedValue);
}

export async function setActiveProfileBackupInterval(interval) {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();
  const normalizedInterval = normalizeBackupInterval(interval);
  await setStoredAppMetaValue(profileBackupIntervalKey(scope.profileId), normalizedInterval);
  return normalizedInterval;
}

export async function chooseActiveProfileBackupTarget() {
  if (typeof window.showDirectoryPicker !== "function") {
    throw new Error("This browser does not support the File System Access directory picker.");
  }

  await initializeWalletWatchDb();
  const backup = await exportActiveProfileBackup();
  const handle = await window.showDirectoryPicker({
    id: "wallet-watch-profile-backup",
    startIn: "downloads"
  });

  await setStoredAppHandle(profileBackupHandleKey(backup.activeProfileId), handle);
  await setProfileBackupAttention(backup.activeProfileId, false);
  return {
    fileName: handle.name ?? "",
    handle
  };
}

export async function reauthorizeActiveProfileBackupTarget() {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();
  const handle = await getStoredAppHandle(profileBackupHandleKey(scope.profileId));

  if (!handle) {
    return {
      status: "missing"
    };
  }

  const permission = await requestWritePermission(handle);
  if (permission !== "granted") {
    await setProfileBackupAttention(scope.profileId, true);
    return {
      status: "denied",
      targetName: handle.name ?? ""
    };
  }

  await setProfileBackupAttention(scope.profileId, false);
  return {
    status: "granted",
    targetName: handle.name ?? ""
  };
}

export async function writeActiveProfileBackupToStoredTarget(reason = "manual-backup", options = {}) {
  const { allowPrompt = true } = options;
  const backupStartedAt = new Date().toISOString();
  await initializeWalletWatchDb();
  const backup = await exportActiveProfileBackup();
  const handle = await getStoredAppHandle(profileBackupHandleKey(backup.activeProfileId));

  if (!handle) {
    return {
      status: "missing"
    };
  }

  let permission = await getWritePermissionStatus(handle);

  if (permission !== "granted" && allowPrompt) {
    permission = await requestWritePermission(handle);
  }

  if (permission !== "granted") {
    await setProfileBackupAttention(backup.activeProfileId, true);
    return {
      status: "denied",
      fileName: handle.name ?? ""
    };
  }

  const payload = {
    ...backup,
    reason
  };
  const fileName = buildProfileBackupFileName(
    backup.profile?.name,
    backup.activeProfileId,
    backup.activeYear
  );
  const fileHandle = await handle.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(`${JSON.stringify(payload, null, 2)}\n`);
  await writable.close();

  const savedAt = new Date().toISOString();
  await Promise.all([
    setStoredAppMetaValue(profileBackupLastSavedAtKey(backup.activeProfileId), savedAt),
    clearProfileBackupDirty(backup.activeProfileId, backupStartedAt),
    setProfileBackupAttention(backup.activeProfileId, false)
  ]);

  return {
    status: "saved",
    fileName,
    targetName: handle.name ?? "",
    savedAt
  };
}

export async function clearActiveProfileBackupTarget() {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();
  await clearStoredAppHandle(profileBackupHandleKey(scope.profileId));
  await clearStoredAppMetaValue(profileBackupLastSavedAtKey(scope.profileId));
  await clearProfileBackupDirty(scope.profileId);
  await setProfileBackupAttention(scope.profileId, false);
}

async function maybeAutoBackupProfile(profileId, reason = "auto-backup") {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();

  if (scope.profileId !== profileId) {
    return {
      status: "ignored"
    };
  }

  const [handle, interval, lastSavedAt, dirtySince] = await Promise.all([
    getStoredAppHandle(profileBackupHandleKey(profileId)),
    getStoredAppMetaValue(profileBackupIntervalKey(profileId)),
    getStoredAppMetaValue(profileBackupLastSavedAtKey(profileId)),
    getStoredAppMetaValue(profileBackupDirtySinceKey(profileId))
  ]);

  if (!dirtySince) {
    return {
      status: "clean"
    };
  }

  if (!handle) {
    return {
      status: "missing"
    };
  }

  const normalizedInterval = normalizeBackupInterval(interval);
  const lastSavedAtMs = Number(new Date(lastSavedAt ?? "").getTime());
  const nowMs = Date.now();
  const intervalMs = PROFILE_BACKUP_INTERVAL_MS[normalizedInterval];

  if (Number.isFinite(lastSavedAtMs) && nowMs - lastSavedAtMs < intervalMs) {
    return {
      status: "throttled",
      retryAfterMs: intervalMs - (nowMs - lastSavedAtMs)
    };
  }

  return writeActiveProfileBackupToStoredTarget(reason, { allowPrompt: false });
}

function clearScheduledProfileBackup(profileId) {
  const scheduledTimer = scheduledProfileBackups.get(profileId);

  if (!scheduledTimer) {
    return;
  }

  clearTimeout(scheduledTimer);
  scheduledProfileBackups.delete(profileId);
}

function scheduleMaybeAutoBackup(profileId, reason, delayMs = AUTO_BACKUP_CHANGE_DELAY_MS) {
  if (!profileId) {
    return;
  }

  clearScheduledProfileBackup(profileId);
  const safeDelayMs = Math.max(0, Number(delayMs) || 0);
  const timer = setTimeout(async () => {
    scheduledProfileBackups.delete(profileId);

    try {
      const result = await maybeAutoBackupProfile(profileId, reason);

      if (result.status === "throttled") {
        scheduleMaybeAutoBackup(profileId, reason, result.retryAfterMs);
      }
    } catch (error) {
      console.error("Automatic backup failed.", error);
    }
  }, safeDelayMs);

  scheduledProfileBackups.set(profileId, timer);
}

async function scheduleDirtyActiveProfileBackup() {
  await initializeWalletWatchDb();
  const scope = await getActiveBudgetScope();
  const dirtySince = await getStoredAppMetaValue(profileBackupDirtySinceKey(scope.profileId));

  if (!dirtySince) {
    return;
  }

  scheduleMaybeAutoBackup(scope.profileId, "startup-dirty-backup", 0);
}

export function installProfileAutoBackup() {
  scheduleDirtyActiveProfileBackup().catch((error) => {
    console.error("Failed to schedule dirty profile backup.", error);
  });

  return subscribeProfileDataChanges(({ profileId, reason, changeType }) => {
    if (changeType !== "domain-data") {
      return;
    }

    markProfileBackupDirty(profileId).catch((error) => {
      console.error("Failed to mark profile backup dirty.", error);
    });
    scheduleMaybeAutoBackup(profileId, reason);
  });
}
